import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Download, RefreshCw, Pencil, Eye, ChevronDown, FileText } from 'lucide-react';
import { Button } from '../components/ui';
import { TranslatedContentViewer } from '../components/review';
import { useCasesStore } from '../store/casesStore';
import { useCasesPolling } from '../hooks';
import { casesApi } from '../api/cases';
import { hasTranslation as checkHasTranslation, hasPdf as checkHasPdf, isApproved as checkIsApproved, canApprove as checkCanApprove, isInReview as checkIsInReview } from '../utils/caseStatus';

export function TranslationPopoutPage() {
  const { caseNumber } = useParams<{ caseNumber: string }>();
  const cases = useCasesStore((state) => state.cases);
  const fetchCases = useCasesStore((state) => state.fetchCases);
  const isLoading = useCasesStore((state) => state.isLoading);
  const approveCase = useCasesStore((state) => state.approveCase);
  const unapproveCase = useCasesStore((state) => state.unapproveCase);

  const [hasFetched, setHasFetched] = useState(false);

  // Memoize the current case to prevent re-renders when other cases change
  const currentCase = useMemo(
    () => cases.find((c) => c.case_number === caseNumber),
    [cases, caseNumber]
  );

  // Translated PDF state
  const [translatedPdfUrl, setTranslatedPdfUrl] = useState<string | null>(null);
  const [translatedPdfLoading, setTranslatedPdfLoading] = useState(false);

  // HTML editor state
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [htmlError, setHtmlError] = useState<string | null>(null);

  // View mode: 'pdf' or 'html'
  const [viewMode, setViewMode] = useState<'pdf' | 'html'>('pdf');

  // Regenerating PDF state
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Download dropdown state
  const [downloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
  const downloadDropdownRef = useRef<HTMLDivElement>(null);

  // Set page title
  useEffect(() => {
    document.title = caseNumber ? `Translation | ${caseNumber}` : 'Translation';
  }, [caseNumber]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target as Node)) {
        setDownloadDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load translated PDF URL
  const loadTranslatedPdfUrl = useCallback(async (cn: string) => {
    setTranslatedPdfLoading(true);
    try {
      const response = await casesApi.getTranslatedPdfUrl(cn);
      setTranslatedPdfUrl(response.url);
    } catch (err) {
      console.error('Failed to get translated PDF URL:', err);
      setTranslatedPdfUrl(null);
    } finally {
      setTranslatedPdfLoading(false);
    }
  }, []);

  // Load HTML content
  const loadHtmlContent = useCallback(async (cn: string) => {
    setHtmlLoading(true);
    setHtmlError(null);
    try {
      const response = await casesApi.getTranslationHtml(cn);
      setHtmlContent(response.html);
    } catch (err: unknown) {
      const error = err as { response?: { status: number } };
      if (error.response?.status === 404) {
        setHtmlContent(null);
      } else {
        setHtmlError('Failed to load translation');
        console.error('Failed to get HTML:', err);
      }
    } finally {
      setHtmlLoading(false);
    }
  }, []);

  // Get current status for polling decision
  const currentStatus = currentCase?.status;

  // Poll when case is in any transitional state
  const needsPolling = currentStatus === 'pending' ||
                       currentStatus === 'translating' ||
                       currentStatus === 'generating_pdf';

  // Poll for case status updates when in transitional states
  useCasesPolling(needsPolling);

  // Track previous status to detect when translation completes
  const prevStatusRef = useRef(currentStatus);
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = currentStatus;

    // When status changes from translating/generating_pdf to ready_for_review, reload content
    if ((prevStatus === 'translating' || prevStatus === 'generating_pdf') &&
        (currentStatus === 'ready_for_review' || currentStatus === 'translated')) {
      if (caseNumber) {
        loadHtmlContent(caseNumber);
        loadTranslatedPdfUrl(caseNumber);
      }
    }
  }, [currentStatus, caseNumber, loadHtmlContent, loadTranslatedPdfUrl]);

  // Fetch cases if store is empty (e.g., on page reload)
  useEffect(() => {
    if (cases.length === 0 && !isLoading && !hasFetched) {
      setHasFetched(true);
      fetchCases();
    }
  }, [cases.length, isLoading, hasFetched, fetchCases]);

  // Load content when case changes
  useEffect(() => {
    if (!caseNumber) return;
    loadHtmlContent(caseNumber);
    loadTranslatedPdfUrl(caseNumber);
  }, [caseNumber, loadHtmlContent, loadTranslatedPdfUrl]);

  // Show loading while fetching cases
  if (isLoading || (cases.length === 0 && !hasFetched)) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-dark-base">
        <span className="inline-block w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mr-2" />
        <span className="text-gray-500 dark:text-gray-400">Loading case data...</span>
      </div>
    );
  }

  if (!currentCase) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-dark-base">
        <div className="text-gray-500 dark:text-gray-400">Case not found</div>
      </div>
    );
  }

  const isApproved = checkIsApproved(currentStatus);
  const canApprove = checkCanApprove(currentStatus);
  const hasTranslation = checkHasTranslation(currentStatus);
  const hasPdf = checkHasPdf(currentStatus);
  const isInReview = checkIsInReview(currentStatus);
  const isCaseTranslating = currentStatus === 'translating';
  const isGeneratingPdf = currentStatus === 'generating_pdf' || isRegenerating;

  const handleApprove = async () => {
    if (!caseNumber) return;
    await approveCase(caseNumber);
  };

  const handleUnapprove = async () => {
    if (!caseNumber) return;
    await unapproveCase(caseNumber);
  };

  const handleSaveHtml = async (html: string) => {
    if (!caseNumber) return;
    await casesApi.updateTranslationHtml(caseNumber, html);
    setHtmlContent(html);
    fetchCases();
    // Auto-regenerate PDF and switch to PDF view after saving
    setViewMode('pdf');
    handleRegeneratePdf();
  };

  const handleRegeneratePdf = async () => {
    if (!caseNumber) return;
    setIsRegenerating(true);
    try {
      await casesApi.regeneratePdf(caseNumber);
      fetchCases();
      const checkPdf = setInterval(async () => {
        try {
          const response = await casesApi.getTranslatedPdfUrl(caseNumber);
          setTranslatedPdfUrl(response.url);
          setIsRegenerating(false);
          clearInterval(checkPdf);
          fetchCases();
        } catch {
          // PDF not ready yet, continue polling
        }
      }, 2000);
      setTimeout(() => {
        clearInterval(checkPdf);
        setIsRegenerating(false);
      }, 120000);
    } catch (err) {
      console.error('Failed to regenerate PDF:', err);
      setIsRegenerating(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!caseNumber) return;
    setDownloadDropdownOpen(false);
    try {
      const response = await casesApi.getWordUrl(caseNumber);
      window.open(response.url, '_blank');
    } catch (err) {
      console.error('Failed to get Word URL:', err);
    }
  };

  const handleDownloadHtml = () => {
    if (!caseNumber || !htmlContent) return;
    setDownloadDropdownOpen(false);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${caseNumber}_translation.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-screen bg-white dark:bg-dark-base">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-2 bg-gray-50 dark:bg-dark-elevated border-b border-gray-200 dark:border-dark-border">
        <h3 className="font-medium text-gray-800 dark:text-gray-100">
          Translation - {caseNumber}
        </h3>
        <div className="flex items-center gap-2">
          {hasTranslation && (
            <>
              {isInReview && (
                <>
                  <Button variant="secondary" size="sm" onClick={handleRegeneratePdf} disabled={isGeneratingPdf} isLoading={isGeneratingPdf} title="Regenerate PDF" className="w-20">
                    <RefreshCw className="w-4 h-4 mr-1" />PDF
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setViewMode(viewMode === 'pdf' ? 'html' : 'pdf')} title={viewMode === 'pdf' ? 'Edit HTML' : 'View PDF'} className="w-24">
                    {viewMode === 'pdf' ? <><Pencil className="w-4 h-4 mr-1" />Edit</> : <><Eye className="w-4 h-4 mr-1" />View</>}
                  </Button>
                </>
              )}
              <div className="relative" ref={downloadDropdownRef}>
                <Button variant="secondary" size="sm" onClick={() => setDownloadDropdownOpen(!downloadDropdownOpen)} className="w-28">
                  <Download className="w-4 h-4 mr-1" />Export<ChevronDown className="w-3 h-3 ml-1" />
                </Button>
                {downloadDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-dark-elevated border border-gray-200 dark:border-dark-border rounded-md shadow-lg z-10">
                    <button onClick={handleDownloadWord} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-hover flex items-center gap-2" disabled={!hasPdf}>
                      <FileText className="w-4 h-4" />Word
                    </button>
                    <button onClick={handleDownloadHtml} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-hover flex items-center gap-2" disabled={!htmlContent}>
                      <FileText className="w-4 h-4" />HTML
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {isApproved && (
            <Button variant="secondary" size="sm" onClick={handleUnapprove} className="w-24">Unapprove</Button>
          )}
          {canApprove && (
            <Button variant="primary" size="sm" onClick={handleApprove} className="w-24">Approve</Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <TranslatedContentViewer
          isCaseTranslating={isCaseTranslating}
          isGeneratingPdf={isGeneratingPdf}
          hasTranslation={hasTranslation}
          hasPdf={hasPdf}
          viewMode={viewMode}
          htmlLoading={htmlLoading}
          htmlError={htmlError}
          htmlContent={htmlContent}
          translatedPdfLoading={translatedPdfLoading}
          translatedPdfUrl={translatedPdfUrl}
          isApproved={isApproved}
          onSaveHtml={handleSaveHtml}
          onSetViewMode={setViewMode}
        />
      </div>
    </div>
  );
}
